<template>
  <div class="aba-discount-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Discount Summary Report</h4>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading discount summary...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Discount Summary Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
            <div class="filter-info-line mt-1" v-if="filterSummary">
              {{ filterSummary }}
            </div>
          </div>
        </div>
      </div>

      <h5 class="section-title mb-3">Department-wise Breakdown</h5>
      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-left">Department</th>
              <th class="text-center">Students</th>
              <th class="text-right">Actual Fees</th>
              <th class="text-right">Discount</th>
              <th class="text-right">Net Payable</th>
              <th class="text-center">Disc %</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in breakdown" :key="index">
              <td class="text-left font-weight-bold">
                {{ row.departmentName || "—" }}
              </td>
              <td class="text-center">{{ row.studentCount || 0 }}</td>
              <td class="text-right">
                {{ formatAmount(row.totalActualFees) }}
              </td>
              <td class="text-right">
                {{ formatAmount(row.totalDiscountAmount) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(row.netPayableFees) }}
              </td>
              <td class="text-center">
                {{ formatPercent(row.discountPercentage) }}
              </td>
            </tr>
            <tr v-if="!breakdown.length">
              <td colspan="6" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No department breakdown found for the selected criteria</p>
              </td>
            </tr>
            <tr v-if="summary && breakdown.length" class="totals-row">
              <td class="text-right font-weight-bold">Grand Totals</td>
              <td class="text-center font-weight-bold">
                {{ summary.totalStudents || 0 }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(summary.totalActualFees) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(summary.totalDiscountAmount) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(summary.netPayableFees) }}
              </td>
              <td class="text-center font-weight-bold">
                {{ formatPercent(summary.discountPercentage) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "DiscountSummaryPrint",
  components: { BButton, BSpinner },
  data() {
    return {
      loading: false,
      error: null,
      summary: null,
      breakdown: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      classID: 0,
      sectionID: 0,
      departmentID: 0,
      className: "",
      sectionName: "",
      departmentName: "",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    filterSummary() {
      const parts = [];
      parts.push(this.className ? `Class: ${this.className}` : "Class: All");
      parts.push(
        this.sectionName ? `Section: ${this.sectionName}` : "Section: All"
      );
      parts.push(
        this.departmentName
          ? `Department: ${this.departmentName}`
          : this.departmentID
          ? `Department ID: ${this.departmentID}`
          : "Department: All"
      );
      return parts.join(" | ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      if (this.reportDomain !== "myskoolaba") {
        this.error = "This report is only available for Myskool ABA.";
        return;
      }
      const q = this.$route.query;
      this.db = q.db || this.$store.state.userData.db;
      this.cID = Number(q.cID || this.$store.state.userData.cId || 0);
      this.classID = Number(q.classID || 0);
      this.sectionID = Number(q.sectionID || 0);
      this.departmentID = Number(q.departmentID || 0);
      this.className = q.className || "";
      this.sectionName = q.sectionName || "";
      this.departmentName = q.departmentName || "";
      if (!this.db) {
        this.error = "Database identifier is missing.";
        return;
      }
      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );
        if (
          response.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branch =
          (this.$store.state.branches || []).find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (branch) {
          this.campusName =
            branch.organizationName || branch.branch || "MY SKOOL";
          this.campusBranch = branch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${this.$store.state.domain}Fees/DiscountSummaryReport`,
          {
            params: {
              db: this.db,
              cID: this.cID,
              classID: this.classID || 0,
              sectionID: this.sectionID || 0,
              departmentID: this.departmentID || 0,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );
        if (response.data?.status === "success") {
          this.summary = response.data.summary || null;
          this.breakdown = response.data.departmentWiseBreakdown || [];
        } else {
          this.error =
            response.data?.message || "Unexpected response from server.";
        }
      } catch (err) {
        console.error(err);
        this.error = "Failed to retrieve report data.";
      } finally {
        this.loading = false;
      }
    },
    formatAmount(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "-";
      return num.toLocaleString("en-PK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatPercent(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "-";
      return `${num.toFixed(2)}%`;
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped src="./abaDiscountReportPrint.scss"></style>
